/**
 * Monedas soportadas por ManuSplit.
 * Fuente: ISO 4217.
 * `decimals` es el número de decimales fraccionarios de la unidad menor.
 * Se usa para conversión entre céntimos (bigint) y unidad mayor (número humano).
 */
export interface Currency {
  code: string;
  name: string;
  symbol: string;
  decimals: number;
}

export const CURRENCIES = {
  EUR: { code: 'EUR', name: 'Euro', symbol: '€', decimals: 2 },
  USD: { code: 'USD', name: 'US Dollar', symbol: '$', decimals: 2 },
  GBP: { code: 'GBP', name: 'British Pound', symbol: '£', decimals: 2 },
  JPY: { code: 'JPY', name: 'Japanese Yen', symbol: '¥', decimals: 0 },
  CHF: { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', decimals: 2 },
  CAD: { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$', decimals: 2 },
  AUD: { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', decimals: 2 },
  MXN: { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$', decimals: 2 },
  BRL: { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', decimals: 2 },
  CNY: { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', decimals: 2 },
  INR: { code: 'INR', name: 'Indian Rupee', symbol: '₹', decimals: 2 },
  KRW: { code: 'KRW', name: 'South Korean Won', symbol: '₩', decimals: 0 },
  SEK: { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', decimals: 2 },
  NOK: { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', decimals: 2 },
  DKK: { code: 'DKK', name: 'Danish Krone', symbol: 'kr', decimals: 2 },
  PLN: { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', decimals: 2 },

  // --- No seleccionables (ver NON_SELECTABLE abajo) --------------------------
  ARS: { code: 'ARS', name: 'Argentine Peso', symbol: 'AR$', decimals: 2 },
  BHD: { code: 'BHD', name: 'Bahraini Dinar', symbol: 'BD', decimals: 3 },
} as const satisfies Record<string, Currency>;

export type CurrencyCode = keyof typeof CURRENCIES;

/**
 * Divisas que el BCE no cotiza, y que por tanto no se pueden convertir nunca:
 * no publica `BHD` en absoluto y dejó de publicar `ARS` el 2020-10-30
 * (ADR-0011). Desde `20260804140000` no están en la tabla `currencies`, así que
 * el servidor rechazaría cualquier fila que las use.
 *
 * Se quedan aquí, fuera del desplegable pero con sus metadatos, por un motivo
 * concreto: **`BHD` es la única divisa de tres decimales que conocemos**, y es
 * el único caso que ejercita la conversión con decimales distintos de dos — la
 * lógica que arregló que 10 € se convirtieran en ¥186.270 en vez de ¥1.863.
 * Borrarla del mapa deja ese camino sin una sola prueba real.
 */
export const NON_SELECTABLE_CURRENCY_CODES = ['ARS', 'BHD'] as const;

/** Todas las conocidas, para leer y formatear cualquier fila histórica. */
export const CURRENCY_CODES = Object.keys(CURRENCIES) as CurrencyCode[];

/** Las que se ofrecen al usuario. Es la lista que deben usar los selectores. */
export const SELECTABLE_CURRENCY_CODES = CURRENCY_CODES.filter(
  (c) => !(NON_SELECTABLE_CURRENCY_CODES as readonly string[]).includes(c)
);

export function getCurrency(code: string): Currency {
  const currency = lookup(code);
  if (!currency) {
    throw new Error(`Unknown currency code: ${code}`);
  }
  return currency;
}

/**
 * Buscar en la tabla mirando **sólo sus propias claves**.
 *
 * Un objeto literal hereda de `Object.prototype`, así que `CURRENCIES['constructor']`
 * no es `undefined`: es una función. Sin este filtro, `getCurrencyOrNull` podía
 * devolver algo que no es un `Currency` —contradiciendo su propio tipo— y
 * `getCurrency` no lanzaba con un código desconocido.
 *
 * Hoy no llega a hacer daño: el único que la llama es el importador, que sólo
 * lee `.decimals` y cae a 2. Se cierra porque el siguiente que la use puede
 * mirar si el resultado existe para decidir si la divisa vale, y ahí `constructor`
 * pasaría por divisa buena.
 */
function lookup(code: string): Currency | undefined {
  return Object.hasOwn(CURRENCIES, code)
    ? (CURRENCIES as Record<string, Currency>)[code]
    : undefined;
}

/**
 * Como `getCurrency`, pero devuelve `undefined` en vez de lanzar.
 *
 * Para cuando el código viene de fuera y aún no se ha validado — el CSV del
 * importador, por ejemplo, donde una divisa mal escrita es un caso normal que
 * se rechaza con su motivo, no una excepción. `getCurrency` sigue lanzando a
 * propósito: en el resto del código, un código desconocido es un fallo.
 */
export function getCurrencyOrNull(code: string): Currency | undefined {
  return lookup(code);
}

